/**
 * Live regions — TASK 7.3 Step 4.
 *
 * Two polite regions, extracted verbatim from the tail of the mock's shell:
 *
 *   1. the TOAST RAIL   (mock L828-832) —
 *      `<div aria-live="polite" aria-atomic="false" style="…bottom:{{ toastBottom }}…">`
 *      holding `{{ toasts }}` cards (L830). `toastBottom` is one of the mock's
 *      61 dynamic bindings: `calc(80px + env(safe-area-inset-bottom))` idle,
 *      `calc(150px + …)` while the mini-player is docked (mock L955) — applied
 *      here with CSSOM, never a style string (styles/README "Dynamic bindings").
 *
 *   2. the ANNOUNCER    (mock L834) —
 *      `<span aria-live="polite" class="sm-visually-hidden">{{ liveMsg }}</span>`,
 *      the sink for `state.live` (auto-advance, favorite toggle, search counts,
 *      download status — §8 dynamic announcements).
 *
 * Toast tones follow the mock's `_toast(text, tone)` (L888): err -> pink card
 * with "⚠", ok -> teal with "✓", info -> yellow with "ℹ", label always ink.
 * Status is therefore carried by icon + text as well as colour (§8.5).
 *
 * @module
 */

import { append, el } from '../components/primitives/dom.js';

/** mock L888 — tone -> [background, icon]. Ink label colour on all three. */
const TONE = {
  err: { background: 'var(--sm-color-pink)', icon: '⚠' },
  ok: { background: 'var(--sm-color-teal)', icon: '✓' },
  info: { background: 'var(--sm-color-yellow)', icon: 'ℹ' },
};

/** mock L888 — toasts auto-dismiss after 3.6s. */
export const TOAST_TTL_MS = 3600;

/** mock L955 — the two `toastBottom` values. */
export const TOAST_OFFSET_IDLE = 'var(--sm-toast-offset)';
export const TOAST_OFFSET_PLAYING = 'var(--sm-toast-offset-playing)';

/**
 * @typedef {object} LiveRegionsHandle
 * @property {HTMLElement} toastRail the `aria-live=polite` toast container
 * @property {HTMLElement} announcer the visually-hidden `aria-live=polite` span
 * @property {HTMLElement[]} elements both, in mount order
 * @property {(text: string, tone?: 'ok'|'err'|'info') => () => void} toast
 *   shows a toast; returns a dismiss function (also auto-dismisses)
 * @property {(text: string) => void} announce sets the polite announcer
 * @property {(playing: boolean) => void} setDocked raises the rail above the
 *   mini-player when a track is loaded
 * @property {() => void} dispose clears pending auto-dismiss timers
 */

/**
 * Builds (does not mount) the two live regions.
 *
 * @param {{ ttlMs?: number }} [options]
 * @returns {LiveRegionsHandle}
 */
export function createLiveRegions(options = {}) {
  const { ttlMs = TOAST_TTL_MS } = options;

  // mock L828
  const toastRail = el('div', {
    className: 'sm-toast-rail',
    attrs: { 'aria-live': 'polite', 'aria-atomic': 'false' },
    styleProps: { bottom: TOAST_OFFSET_IDLE },
  });

  // mock L834
  const announcer = el('span', {
    className: 'sm-visually-hidden',
    attrs: { 'aria-live': 'polite' },
  });

  /** @type {Set<ReturnType<typeof setTimeout>>} */
  const timers = new Set();

  /**
   * @param {string} text
   * @param {'ok'|'err'|'info'} [tone]
   * @returns {() => void}
   */
  function toast(text, tone = 'info') {
    const { background, icon } = TONE[tone] ?? TONE.info;
    // mock L830
    const card = el('div', { className: 'sm-toast', styleProps: { background } });
    append(
      card,
      el('span', { className: 'sm-toast__icon', text: icon, attrs: { 'aria-hidden': 'true' } }),
      el('span', { className: 'sm-toast__text', text }),
    );
    toastRail.append(card);
    // The mock ALSO mirrors every toast into `state.live` (L888) so screen
    // readers hear it even though the rail is visual-first.
    announce(text);

    const dismiss = () => {
      card.remove();
    };
    const timer = setTimeout(() => {
      timers.delete(timer);
      dismiss();
    }, ttlMs);
    timers.add(timer);
    return dismiss;
  }

  /** @param {string} text */
  function announce(text) {
    announcer.textContent = text;
  }

  /** @param {boolean} docked */
  function setDocked(docked) {
    // CSSOM, not a style string — styles/README "Dynamic bindings".
    toastRail.style.setProperty('bottom', docked ? TOAST_OFFSET_PLAYING : TOAST_OFFSET_IDLE);
  }

  function dispose() {
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
  }

  return {
    toastRail,
    announcer,
    elements: [toastRail, announcer],
    toast,
    announce,
    setDocked,
    dispose,
  };
}
